/**
 * File: QuoteModal.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v1.3.6
 * Created: 2026-01-06
 * Last Updated: 2026-06-04
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useState, useEffect } from "react";

// Local project files
import { TextButton } from "../ui/Buttons";
import { quoteModal } from "../../schemas/components/quoteModal";
import Checkbox from "../forms/Checkbox";
import CheckList from "../forms/CheckList";
import DropDownField from "../forms/DropDownField";
import EmailField from "../forms/EmailField";
import FileUploadField from "../forms/FileUploadField";
import FormHeader from "../forms/FormHeader";
import ModalOverlay from "../ui/ModalOverlay";
import PhoneNumberField from "../forms/PhoneNumberField";
import StateField from "../forms/StateField";
import TextField from "../forms/TextField";

/**
 * [TODO: Component description here]
 *
 * @param {Object} props - Component properties
 * @returns {JSX.Element} [TODO: Rendered output]
 */
function QuoteModal({ onClose }) {
    const [companyName, setCompanyName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");

    const [services, setServices] = useState([]);
    const [disciplines, setDisciplines] = useState([]);
    const [packageSelection, setPackageSelection] = useState("");
    const [timeline, setTimeline] = useState("");

    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [type, setType] = useState("");
    const [size, setSize] = useState("");
    const [scope, setScope] = useState("");
    const [files, setFiles] = useState([]);

    const [consent, setConsent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        onClose();

        const formData = new FormData();

        formData.append("companyName", companyName);
        formData.append("firstName", firstName);
        formData.append("lastName", lastName);
        formData.append("phoneNumber", phoneNumber);
        formData.append("email", email);
        formData.append("services", services);
        formData.append("disciplines", disciplines);
        formData.append("packageSelection", packageSelection);
        formData.append("timeline", timeline);
        formData.append("city", city);
        formData.append("state", state);
        formData.append("type", type);
        formData.append("size", size);
        formData.append("scope", scope);
        formData.append("consent", consent);

        if (files.length > 0) {
            files.forEach((file) => {
                formData.append("files", file);
            });
        }

        try {
            await fetch(
                `${process.env.REACT_APP_NOTIFICATION_URL}/emails/quote`,
                {
                    method: "POST",
                    body: formData,
                },
            );
        } catch (error) {
            console.error("Error submitting quote form:", error);
        }
    };

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <div className="quote-modal">
                <form onSubmit={handleSubmit} className="quote-modal__form">
                    <FormHeader
                        header={quoteModal.formHeader.header}
                        subheader={quoteModal.formHeader.subheader}
                        closeButton={true}
                        onClick={onClose}
                        alignLeft={true}
                    />
                    <fieldset>
                        <div className="quote-modal_section-title">
                            <h4>{quoteModal.headers.contactInformation}</h4>
                            <hr></hr>
                        </div>
                        <TextField
                            label={quoteModal.textField.companyName.label}
                            text={companyName}
                            setText={setCompanyName}
                        />
                        <div className="quote-modal__row-container">
                            <div className="quote-modal__column-container">
                                <TextField
                                    label={quoteModal.textField.firstName.label}
                                    text={firstName}
                                    setText={setFirstName}
                                />
                            </div>
                            <div className="quote-modal__column-container">
                                <TextField
                                    label={quoteModal.textField.lastName.label}
                                    text={lastName}
                                    setText={setLastName}
                                />
                            </div>
                        </div>
                        <div className="quote-modal__row-container">
                            <div className="quote-modal__column-container">
                                <PhoneNumberField
                                    phoneNumber={phoneNumber}
                                    setPhoneNumber={setPhoneNumber}
                                />
                            </div>
                            <div className="quote-modal__column-container">
                                <EmailField email={email} setEmail={setEmail} />
                            </div>
                        </div>
                        <div className="quote-modal_section-title">
                            <h4>{quoteModal.headers.serviceSelection}</h4>
                            <hr></hr>
                        </div>
                        <div className="quote-modal__row-container">
                            <div className="quote-modal__column-container">
                                <CheckList
                                    heading={
                                        quoteModal.checkList.services.heading
                                    }
                                    checkboxes={
                                        quoteModal.checkList.services.checkboxes
                                    }
                                    selected={services}
                                    setSelected={setServices}
                                />
                            </div>
                            <div className="quote-modal__column-container">
                                <CheckList
                                    heading={
                                        quoteModal.checkList.disciplines.heading
                                    }
                                    checkboxes={
                                        quoteModal.checkList.disciplines
                                            .checkboxes
                                    }
                                    selected={disciplines}
                                    setSelected={setDisciplines}
                                />
                            </div>
                        </div>
                        <div className="quote-modal__row-container">
                            <div className="quote-modal__column-container">
                                <DropDownField
                                    label={
                                        quoteModal.dropDownField.package.label
                                    }
                                    options={
                                        quoteModal.dropDownField.package.options
                                    }
                                    selection={packageSelection}
                                    setSelection={setPackageSelection}
                                />
                            </div>
                            <div className="quote-modal__column-container">
                                <DropDownField
                                    label={
                                        quoteModal.dropDownField.timeline.label
                                    }
                                    options={
                                        quoteModal.dropDownField.timeline
                                            .options
                                    }
                                    selection={timeline}
                                    setSelection={setTimeline}
                                />
                            </div>
                        </div>
                        <div className="quote-modal_section-title">
                            <h4>{quoteModal.headers.projectDetails}</h4>
                            <hr></hr>
                        </div>
                        <div className="quote-modal__row-container">
                            <div className="quote-modal__column-container">
                                <TextField
                                    label={quoteModal.textField.city.label}
                                    text={city}
                                    setText={setCity}
                                />
                            </div>
                            <div className="quote-modal__column-container">
                                <StateField
                                    label={quoteModal.stateField.label}
                                    state={state}
                                    setState={setState}
                                />
                            </div>
                        </div>
                        <div className="quote-modal__row-container">
                            <div className="quote-modal__column-container">
                                <DropDownField
                                    label={quoteModal.dropDownField.type.label}
                                    options={
                                        quoteModal.dropDownField.type.options
                                    }
                                    selection={type}
                                    setSelection={setType}
                                />
                            </div>
                            <div className="quote-modal__column-container">
                                <DropDownField
                                    label={quoteModal.dropDownField.size.label}
                                    options={
                                        quoteModal.dropDownField.size.options
                                    }
                                    selection={size}
                                    setSelection={setSize}
                                />
                            </div>
                        </div>
                        <TextField
                            label={quoteModal.textField.scope.label}
                            text={scope}
                            setText={setScope}
                        />
                        <FileUploadField
                            label={quoteModal.fileUploadField.label}
                            files={files}
                            setFiles={setFiles}
                        />
                        <div>
                            <Checkbox
                                id={quoteModal.checkbox.consent.id}
                                label={quoteModal.checkbox.consent.label}
                                checked={consent}
                                onChange={setConsent}
                                isRequired={true}
                            />
                        </div>
                    </fieldset>
                    <TextButton
                        text={quoteModal.textButton.text}
                        type={quoteModal.textButton.type}
                    />
                </form>
            </div>
        </>
    );
}

export default QuoteModal;
